import React from 'react';
import { CalendarEventHelper } from './useCalendarEvents';
import Stack from '../Stack';
import { getIntl } from '../../../utils/intl';
import Text from '../Text';
import ResourceCard from '../../shared/ResourceCard';
import { AuditLogLogCard } from '../../shared/LogList/LogCard';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import { EventType } from './types';

const intl = getIntl('DayEventsList', {
    visits: 'Visits',
    recordUpdates: 'Records',
    collaboration: 'Collaboration',
    profile: 'Profile',
});

export type DayEventListOwnProps = {
    isoDate: string;
    calendarEventsHelper: CalendarEventHelper;
    profileId: string;
    onClosePromptOrSideDrawer: () => void;
    eventType: EventType;
};

export type DayEventListProps = DayEventListOwnProps;

const DayEventList: React.FC<DayEventListProps> = ({
    calendarEventsHelper,
    isoDate,
    profileId,
    onClosePromptOrSideDrawer,
    eventType,
}) => {
    const { navigate } = useProfilesTabNavigation();

    if (!calendarEventsHelper.getHasEvents(isoDate, eventType)) {
        return null;
    }

    const { visits, profile, recordUpdates, collaboration } = calendarEventsHelper.getEvents(isoDate);

    const renderSection = (title: string, content: React.ReactNode) => (
        <Stack vertical space={2} style={{ alignSelf: 'stretch' }}>
            <Text variant="body-1" color="Gray-Text">
                {title}
            </Text>
            {content}
        </Stack>
    );

    return (
        <Stack space={12} vertical style={{ alignSelf: 'stretch' }}>
            {visits.length > 0 && (eventType === 'all' || eventType === 'visits')
                ? renderSection(
                      intl('visits'),
                      <>
                          {visits.map((visit) => (
                              <ResourceCard
                                  key={visit.id}
                                  resource={visit}
                                  category="visit"
                                  profileId={profileId}
                                  onPress={() => {
                                      onClosePromptOrSideDrawer();
                                      navigate('data-details', {
                                          resourceId: visit.id,
                                          profileId,
                                          category: 'visit',
                                      });
                                  }}
                                  flat
                              />
                          ))}
                      </>,
                  )
                : null}
            {recordUpdates.length > 0 && (eventType === 'all' || eventType === 'recordUpdates')
                ? renderSection(
                      intl('recordUpdates'),
                      <>
                          {recordUpdates.map((auditLog) => (
                              <AuditLogLogCard
                                  key={auditLog.auditLogId}
                                  log={auditLog}
                                  onPress={() => {
                                      onClosePromptOrSideDrawer();
                                      navigate('profile-activity-details', {
                                          auditLogId: auditLog.auditLogId,
                                          profileId,
                                      });
                                  }}
                                  flat
                              />
                          ))}
                      </>,
                  )
                : null}
            {profile.length > 0 && (eventType === 'all' || eventType === 'profile')
                ? renderSection(
                      intl('profile'),
                      <>
                          {profile.map((auditLog) => (
                              <AuditLogLogCard
                                  key={auditLog.auditLogId}
                                  log={auditLog}
                                  onPress={() => {
                                      onClosePromptOrSideDrawer();
                                      navigate('profile-activity-details', {
                                          auditLogId: auditLog.auditLogId,
                                          profileId,
                                      });
                                  }}
                                  flat
                              />
                          ))}
                      </>,
                  )
                : null}
            {collaboration.length > 0 && (eventType === 'all' || eventType === 'collaboration')
                ? renderSection(
                      intl('collaboration'),
                      <>
                          {collaboration.map((auditLog) => (
                              <AuditLogLogCard
                                  key={auditLog.auditLogId}
                                  log={auditLog}
                                  onPress={() => {
                                      onClosePromptOrSideDrawer();
                                      navigate('profile-activity-details', {
                                          auditLogId: auditLog.auditLogId,
                                          profileId,
                                      });
                                  }}
                                  flat
                              />
                          ))}
                      </>,
                  )
                : null}
        </Stack>
    );
};

export default React.memo(DayEventList);
